import React from "react";
import ReactApexChart from "react-apexcharts";
import { Box, Typography, Paper } from "@mui/material";

const BudgetCharts = ({
  data,
  currency = "USD",
  totalBudget = 0,
  startDate,
  endDate,
}) => {
  console.log("🚀 ~ BudgetCharts ~ data:", data);
  console.log("🚀 ~ BudgetCharts ~ totalBudget:", totalBudget);
  console.log("🚀 ~ BudgetCharts ~ startDate:", startDate);
  console.log("🚀 ~ BudgetCharts ~ endDate:", endDate);

  // Transform data for ApexCharts
  const chartData = React.useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      console.log("❌ No data or empty array");
      return {
        categories: [],
        usage: [],
        forecast: [],
        forecastedAggregate: [],
        monthlyBudget: [],
        totalBudgetLine: [],
      };
    }

    // Calculate daily budget and monthly budget amounts
    const calculateMonthlyBudgets = () => {
      if (!startDate || !endDate || typeof totalBudget !== "number") {
        console.log("❌ Missing budget period information, using fallback calculation");
        return data.map(() => totalBudget / (data.length || 1));
      }
    
      // ---------- UTC helpers ----------
      const MS_PER_DAY = 24 * 60 * 60 * 1000;
    
      const parseYMDAsUTC = (ymd) => {
        // "YYYY-MM-DD" -> Date at UTC midnight
        const [y, m, d] = ymd.split("-").map(Number);
        return new Date(Date.UTC(y, m - 1, d));
      };
    
      const startOfMonthUTC = (year, monthIdx0) => new Date(Date.UTC(year, monthIdx0, 1));
      const endOfMonthUTC   = (year, monthIdx0) => new Date(Date.UTC(year, monthIdx0 + 1, 0)); // last day
    
      const daysInclusive = (aUTC, bUTC) => {
        if (bUTC < aUTC) return 0;
        return Math.floor((bUTC.getTime() - aUTC.getTime()) / MS_PER_DAY) + 1;
      };
    
      // Allocate totalCents proportionally to integer 'parts' and ensure exact sum via largest remainders
      const allocateProportionallyCents = (totalCents, parts) => {
        const sumParts = parts.reduce((a, b) => a + b, 0);
        if (sumParts === 0) return parts.map(() => 0);
        const raw = parts.map((p) => (p * totalCents) / sumParts);
        const floored = raw.map((x) => Math.floor(x));
        let remainder = totalCents - floored.reduce((a, b) => a + b, 0);
        const order = raw
          .map((x, i) => ({ i, frac: x - Math.floor(x) }))
          .sort((a, b) => b.frac - a.frac);
        for (let k = 0; k < remainder; k++) floored[order[k].i] += 1;
        return floored; // in cents
      };
    
      // ---------- Parse start/end as UTC ----------
      const start = parseYMDAsUTC(startDate);
      const end   = parseYMDAsUTC(endDate);
    
      // ---------- Count days per month within [start, end] inclusive (UTC) ----------
      const daysPerMonth = data.map((item) => {
        const [y, m] = item.month.split("-").map(Number);
        const mStart = startOfMonthUTC(y, m - 1);
        const mEnd   = endOfMonthUTC(y, m - 1);
        const overlapStart = new Date(Math.max(start.getTime(), mStart.getTime()));
        const overlapEnd   = new Date(Math.min(end.getTime(),   mEnd.getTime()));
        return daysInclusive(overlapStart, overlapEnd);
      });
    
      const totalDays = daysPerMonth.reduce((a, b) => a + b, 0);
      if (totalDays === 0) return data.map(() => 0);
    
      // Daily (for reference/use elsewhere if needed)
      const dailyBudget = totalBudget / totalDays;
    
      // ---------- Exact-sum monthly allocation in cents ----------
      const totalCents = Math.round(totalBudget * 100);
      const centsPerMonth = allocateProportionallyCents(totalCents, daysPerMonth);
      const monthlyBudgets = centsPerMonth.map((c) => c / 100);
    
      // Optional sanity logs
      console.log("📊 totalDays:", totalDays, "dailyBudget:", dailyBudget);
      console.log("Σ monthly =", monthlyBudgets.reduce((a,b)=>a+b,0));
    
      return monthlyBudgets;
    };
    

    const monthlyBudget = calculateMonthlyBudgets();

    // Calculate actual spend aggregate (only for past months)
    let actualCumulative = 0;
    const actualAggregate = data.map((item) => {
      if (item.future === false) {
        // Past month: add actual usage
        actualCumulative += item.usage || 0;
        console.log(
          `📈 Month ${item.month} (past): usage=${item.usage}, actual cumulative=${actualCumulative}`
        );
        return actualCumulative;
      } else {
        // Future month: return null to break the line
        return null;
      }
    });

    // Calculate forecasted aggregate spend (only forecast values)
    let forecastCumulative = actualCumulative; // Start from actual cumulative, only add forecast values
    const forecastedAggregate = data.map((item) => {
      if (item.future === false && !item.partial) {
        // Past month (not partial): return null to break the line
        return null;
      } else {
        // Future month OR partial month: add forecast to cumulative
        const forecastValue = item.forecast || 0;
        forecastCumulative += forecastValue;
        console.log(
          `🔮 Month ${item.month} (${
            item.future ? "future" : "partial"
          }): forecast=${forecastValue}, forecast cumulative=${forecastCumulative}`
        );
        return forecastCumulative;
      }
    });

    // Create total budget line (constant value)
    const totalBudgetLine = data.map(() => totalBudget);

    const result = {
      categories: data.map((item) => {
        // Parse the month string properly (e.g., "2025-05" -> May 25)
        const [year, month] = item.month.split("-");
        const date = new Date(parseInt(year), parseInt(month) - 1, 1); // month is 0-indexed
        return date.toLocaleDateString("en-US", {
          month: "short",
          year: "2-digit",
        });
      }),
      usage: data.map((item) => item.usage || 0),
      forecast: data.map((item) => item.forecast || 0),
      actualAggregate,
      forecastedAggregate,
      monthlyBudget,
      totalBudgetLine,
    };

    console.log("📊 Chart data result:", result);
    return result;
  }, [data, totalBudget, startDate, endDate]);

  // ApexCharts configuration
  const chartOptions = {
    chart: {
      type: "line",
      height: "100%",
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        endingShape: "rounded",
      },
    },

    colors: ["#3b82f6", "#f97316", "#3b82f6", "#f97316", "#8b5cf6", "#dc2626"],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: [0, 0, 3, 3, 2, 3], // 0 for bars, 3 for lines
      dashArray: [0, 0, 5, 5, 3, 0], // 0 for solid, 5 for dashed, 5 for dashed, 3 for dotted, 0 for solid
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 5,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          colors: "#64748b",
          fontSize: "12px",
          fontFamily: "Inter, sans-serif",
        },
        rotate: -45,
        rotateAlways: false,
        maxHeight: 60,
        trim: true,
        hideOverlappingLabels: true,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tickAmount: "dataPoints",
    },
    yaxis: {
      labels: {
        formatter: function (value) {
          return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value);
        },
        style: {
          colors: "#64748b",
          fontSize: "12px",
          fontFamily: "Inter, sans-serif",
        },
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      theme: "light",
      style: {
        fontSize: "12px",
      },
      x: {
        show: true,
        format: "MMM yy",
      },
      y: {
        formatter: function (value) {
          return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value);
        },
      },
      marker: {
        show: true,
      },
      fixed: {
        enabled: false,
      },
      followCursor: true,
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      labels: {
        colors: "#64748b",
        useSeriesColors: false,
      },
      markers: {
        width: 12,
        height: 12,
        radius: 6,
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 250,
          },
          legend: {
            position: "bottom",
            horizontalAlign: "center",
          },
          xaxis: {
            labels: {
              rotate: -90,
              maxHeight: 80,
              trim: true,
              hideOverlappingLabels: true,
            },
          },
        },
      },
      {
        breakpoint: 480,
        options: {
          chart: {
            height: 200,
          },
          legend: {
            position: "bottom",
            horizontalAlign: "center",
            fontSize: "10px",
          },
          xaxis: {
            labels: {
              rotate: -90,
              maxHeight: 100,
              trim: true,
              hideOverlappingLabels: true,
              fontSize: "10px",
            },
          },
        },
      },
    ],
  };

  const series = [
    {
      name: "Actual Spend",
      type: "column",
      data: chartData.usage.map((value, index) =>
        data[index]?.future === false ? value : null
      ),
    },
    {
      name: "Forecasted Spend",
      type: "column",
      data: chartData.forecast.map((value, index) =>
        data[index]?.future === true || data[index]?.partial === true
          ? value
          : null
      ),
    },
    {
      name: "Aggregated Actual Spend",
      type: "line",
      data: chartData.actualAggregate,
    },
    {
      name: "Aggregated Forecasted Spend",
      type: "line",
      data: chartData.forecastedAggregate,
    },
    {
      name: "Monthly Budget",
      type: "line",
      data: chartData.monthlyBudget,
    },
    {
      name: "Total Budget",
      type: "line",
      data: chartData.totalBudgetLine,
    },
  ];

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        boxShadow:
          "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        backgroundColor: "white",
        height: "100%",
        minHeight: "450px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#1e293b",
            fontSize: { xs: "1rem", sm: "1.125rem" },
            mb: 1,
          }}
        >
          Spend Trend
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
          }}
        >
          Track your monthly spending pattern and forecast
        </Typography>
      </Box>

      {chartData.categories.length > 0 ? (
        <Box
          sx={{
            height: { xs: "280px", sm: "320px", md: "350px" },
            width: "100%",
            flex: 1,
          }}
        >
          <ReactApexChart
            options={chartOptions}
            series={series}
            type="line"
            height="100%"
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: { xs: "280px", sm: "320px", md: "350px" },
            color: "#64748b",
            border: "2px dashed #e2e8f0",
            borderRadius: "8px",
            backgroundColor: "#f8fafc",
            flex: 1,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
              No Data Available
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              Chart data will appear here when available
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default BudgetCharts;
